/**
 * Reglas del aviso «completa tu cumpleaños y tu aniversario» y de sus campos. Solo lógica pura: el servicio
 * consulta al servidor y guarda. Las validaciones reflejan las de `birthdays.rules.ts` del backend, que es quien manda.
 */

export interface BirthState {
  day: number | null;
  month: number | null;
  hidden: boolean;
  /** RR.HH. administra la fecha: la persona no puede cambiarla. */
  locked: boolean;
}

export interface HireState {
  hireDate: string | null;
  hidden: boolean;
  locked: boolean;
}

export interface DatesConfig {
  enabled: boolean;
  anniversariesEnabled: boolean;
}

export interface DatesNeeded {
  birthday: boolean;
  hire: boolean;
}

/** Tras «Más tarde» no se vuelve a preguntar durante un día. */
export const SNOOZE_MS = 24 * 60 * 60 * 1000;

/** Tras una consulta que no encontró nada pendiente, no se vuelve a consultar durante este rato. */
export const RECHECK_MS = 5 * 60 * 1000;

/** Volver a la app tras tanto tiempo en segundo plano cuenta como abrirla de nuevo (la sesión sigue iniciada). */
export const RETURN_AFTER_MS = 30 * 60 * 1000;

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * ¿Qué falta pedirle a la persona? Solo cuentas de empresa con la función encendida; no se pregunta por una fecha
 * que RR.HH. administra ni por una que la persona pidió no celebrar.
 */
export function datesNeeded(input: { cfg: DatesConfig | null; birth: BirthState | null; hire: HireState | null }): DatesNeeded {
  const { cfg, birth, hire } = input;
  if (!cfg || !cfg.enabled) return { birthday: false, hire: false };
  const birthday = !!birth && (birth.day === null || birth.month === null) && !birth.locked && !birth.hidden;
  const needsHire = cfg.anniversariesEnabled && !!hire && !hire.hireDate && !hire.locked && !hire.hidden;
  return { birthday, hire: needsHire };
}

/** Mensaje de error de la fecha de nacimiento, o null si es válida. Día y mes van juntos; el año es opcional. */
export function validateBirth(day: number | null, month: number | null, year: number | null, now = new Date()): string | null {
  if (day === null && month === null && year === null) return null;
  if (day === null || month === null) return 'Elige el día y el mes.';
  if (month < 1 || month > 12 || day < 1 || day > DAYS_IN_MONTH[month - 1]) return 'Esa fecha no existe.';
  if (year !== null) {
    if (!Number.isInteger(year) || year < 1900 || year > now.getFullYear()) return 'El año no es válido.';
    if (month === 2 && day === 29 && !isLeapYear(year)) return 'Esa fecha no existe.';
  }
  return null;
}

/** Mensaje de error de la fecha de ingreso (YYYY-MM-DD), o null si es válida o está vacía. */
export function validateHire(iso: string | null, now = new Date()): string | null {
  if (!iso) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return 'La fecha de ingreso no es válida.';
  const [y, m, d] = iso.split('-').map(Number);
  const real = new Date(Date.UTC(y, m - 1, d));
  if (real.getUTCFullYear() !== y || real.getUTCMonth() !== m - 1 || real.getUTCDate() !== d || y < 1950) {
    return 'La fecha de ingreso no es válida.';
  }
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return iso > today ? 'La fecha de ingreso no puede ser futura.' : null;
}

/** ¿Sigue vigente un «Más tarde» guardado? `stored` es la marca de tiempo (ms) como texto. */
export function isSnoozed(stored: string | null, now = Date.now()): boolean {
  const at = Number(stored);
  return !!stored && Number.isFinite(at) && at > 0 && now - at < SNOOZE_MS;
}

/**
 * ¿Se consulta ahora si falta algún dato? Se pide en varios momentos (abrir la app, volver a ella, cerrar el check-in,
 * terminar una pausa); esto evita consultas repetidas y ventanas dobles.
 */
export function canRequestDatesPrompt(s: { hasCompany: boolean; showing: boolean; busy: boolean; lastEmptyAt: number | null; now: number }): boolean {
  if (!s.hasCompany || s.showing || s.busy) return false;
  return s.lastEmptyAt === null || s.now - s.lastEmptyAt >= RECHECK_MS;
}

/** ¿La persona volvió a la app después de un buen rato en segundo plano? `hiddenAt` es cuándo se ocultó (ms). */
export function returnedToApp(hiddenAt: number | null, now: number): boolean {
  return hiddenAt !== null && now - hiddenAt >= RETURN_AFTER_MS;
}

/** ¿Hubo una pausa nueva? Compara las pausas con Vitalis de hoy; la primera lectura no cuenta (solo fija la base). */
export function pauseCompleted(previous: number | null, current: number | null): boolean {
  return previous !== null && current !== null && current > previous;
}
